import { useEffect, useState, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Menu, X, Phone, Mail, MapPin, Instagram, 
  Star, ChevronRight, Heart, Send, MessageCircle,
  Cake, Cookie, CakeSlice, Croissant, Settings, Leaf, Users
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Toaster, toast } from "sonner";
import AdminApp from "./AdminApp";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Brand Logo URL
const LOGO_URL = "https://staazease.blob.core.windows.net/open-resources/cclogo.jpeg";

// Settings Context
const SettingsContext = createContext(null);
const useSettings = () => useContext(SettingsContext);

// Navbar Component
const Navbar = () => {
  const settings = useSettings();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "My Cottage", href: "#home" },
    { name: "Flavours", href: "#flavours" },
    { name: "My Creations", href: "#menu" },
    { name: "Meet Your Baker", href: "#about" },
    { name: "Why Choose Us", href: "#why-choose" },
    { name: "Client Love", href: "#testimonials" },
    { name: "Get in Touch", href: "#contact" },
  ];

  const whatsappLink = settings ? `https://wa.me/${settings.whatsapp.replace(/[^0-9]/g, '')}` : "#";

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled 
          ? "bg-[#FFEEF5]/95 nav-blur shadow-sm border-b border-[#FFBDD6]/50" 
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <a href="#home" className="flex items-center gap-2" data-testid="logo">
            <div className="h-12 w-12 rounded-xl overflow-hidden bg-white shadow-sm">
              <img 
                src={LOGO_URL} 
                alt="RCake Cottage" 
                className="h-full w-full object-cover"
              />
            </div>
            <span className="font-serif text-xl md:text-2xl text-[#E91E8C] font-semibold tracking-tight hidden sm:block">
              RCake Cottage
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="text-[#4A3540]/80 hover:text-[#E91E8C] transition-colors duration-300 text-sm font-medium tracking-wide"
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </a>
            ))}
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#E91E8C] text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-[#D11A7D] transition-all duration-300 btn-scale"
              data-testid="nav-order-btn"
            >
              Order Now
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 text-[#4A3540]"
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-[#FFEEF5] border-b border-[#FFBDD6]"
          >
            <div className="px-6 py-4 space-y-4">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={() => setIsOpen(false)}
                  className="block text-[#4A3540] hover:text-[#E91E8C] transition-colors py-2 font-medium"
                  data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </a>
              ))}
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-[#E91E8C] text-white px-6 py-3 rounded-full text-center font-medium"
                data-testid="mobile-order-btn"
              >
                Order Now
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section id="home" className="relative min-h-screen flex items-center pt-20" data-testid="hero-section">
      <div className="absolute inset-0 z-0">
        <div 
          className="absolute inset-0 parallax-bg"
          style={{
            backgroundImage: `url('https://staazease.blob.core.windows.net/open-resources/mainpic.png')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#FFEEF5] via-[#FFEEF5]/45 to-[#FFEEF5]/20" />
      </div>
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <span className="inline-block text-[#E91E8C] text-sm font-semibold tracking-widest uppercase mb-4">
              Home Bakery • Den Haag
            </span>
            <h1 className="font-serif text-5xl md:text-7xl text-[#4A3540] font-semibold tracking-tight leading-none mb-6">
              Baked with<br />
              <span className="text-[#E91E8C]">100% Love</span>
            </h1>
            <p className="text-[#4A3540]/70 text-lg md:text-xl leading-relaxed max-w-xl mb-8">
              Handcrafted cakes, cupcakes, cookies, and pastries made fresh with the finest ingredients. 
              Every bite tells a story of passion and dedication.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="#menu"
                className="bg-[#E91E8C] text-white px-8 py-4 rounded-full text-base font-medium hover:bg-[#D11A7D] transition-all duration-300 btn-scale shadow-lg shadow-[#E91E8C]/20 flex items-center gap-2"
                data-testid="hero-explore-btn"
              >
                Explore Our Menu
                <ChevronRight size={18} />
              </a>
              <a
                href="#contact"
                className="bg-[#FFD6E8] text-[#E91E8C] px-8 py-4 rounded-full text-base font-medium hover:bg-[#FFBDD6] transition-all duration-300 btn-scale"
                data-testid="hero-contact-btn"
              >
                Get in Touch
              </a>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="hidden lg:block"
          >
            <div className="relative">
             <div className="absolute -inset-4 bg-[#E91E8C]/10 rounded-3xl blur-2xl"></div>

<img 
  src="https://staazease.blob.core.windows.net/open-resources/Pink%20kid.jpeg"
  alt="Delicious Cake" 
  className="relative z-10 w-full h-96 object-cover rounded-3xl shadow-2xl shadow-[#E91E8C]/20 opacity-0"
/>
              {/* <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-2xl shadow-xl z-20">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#FFD6E8] rounded-full flex items-center justify-center">
                    <Heart className="text-[#E91E8C] fill-[#E91E8C]" size={24} />
                  </div>
                  <div>
                    <p className="font-serif text-lg text-[#4A3540] font-semibold">100% Homemade</p>
                    <p className="text-sm text-[#4A3540]/60">With love & care</p>
                  </div>
                </div>
              </div> */}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Flavour Section
const FlavourSection = () => {
  const flavours = [
    { 
      name: "Red Velvet Base", 
      emoji: "💎",
      gradient: "from-red-700 via-pink-600 to-red-700",
      lightGradient: "from-red-100 via-pink-50 to-red-100",
      hoverGradient: "from-red-600 to-pink-700",
      accent: "#DC2626",
      color: "text-red-800",
      variations: [
        { name: "Vanilla", icon: "🎂" },
        { name: "Vanilla Raspberry Compote", icon: "🫐" },
        { name: "Vanilla Strawberry Compote", icon: "🍓" },
        { name: "Pineapple", icon: "🍍" },
        { name: "Mixed Berries", icon: "🫐" },
      ],
      description: "Elegant and luxurious"
    },
    { 
      name: "Pistachio Base", 
      emoji: "🌿",
      gradient: "from-emerald-700 via-green-600 to-emerald-700",
      lightGradient: "from-emerald-100 via-green-50 to-emerald-100",
      hoverGradient: "from-emerald-600 to-green-700",
      accent: "#059669",
      color: "text-emerald-900",
      isPremium: true,
      variations: [
        { name: "Vanilla", icon: "🎂" },
        { name: "Vanilla Raspberry Compote", icon: "🫐" },
        { name: "Vanilla Strawberry Compote", icon: "🍓" },
        { name: "Pineapple", icon: "🍍" },
        { name: "Mixed Berries", icon: "🫐" },
      ],
      description: "Exquisite & Premium"
    },
    { 
      name: "Vanilla Base", 
      emoji: "🍦",
      gradient: "from-amber-100 via-yellow-50 to-amber-100",
      lightGradient: "from-yellow-50 via-amber-50 to-yellow-50",
      hoverGradient: "from-amber-200 to-yellow-100",
      accent: "#FBBF24",
      color: "text-amber-900",
      variations: [
        { name: "Vanilla", icon: "🎂" },
        { name: "Vanilla Raspberry Compote", icon: "🫐" },
        { name: "Vanilla Strawberry Compote", icon: "🍓" },
        { name: "Pineapple", icon: "🍍" },
        { name: "Mixed Berries", icon: "🫐" },
      ],
      description: "Classic and versatile"
    },
    { 
      name: "Chocolate Base", 
      emoji: "🍫",
      gradient: "from-amber-900 via-orange-700 to-amber-800",
      lightGradient: "from-orange-100 via-yellow-50 to-orange-100",
      hoverGradient: "from-orange-600 to-amber-700",
      accent: "#8B4513",
      color: "text-amber-900",
      variations: [
        { name: "Belgian Chocolate", icon: "🍫" },
        { name: "Chocolate Truffle", icon: "💫" },
        { name: "Black Forest", icon: "🌲" },
      ],
      description: "Rich and decadent"
    },
  ];

  return (
    <section id="flavours" className="relative py-24 md:py-32 bg-gradient-to-b from-white via-[#FFEEF5]/30 to-white overflow-hidden" data-testid="flavour-section">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-10 w-72 h-72 bg-pink-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute -bottom-16 left-10 w-72 h-72 bg-yellow-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <span className="inline-block text-[#E91E8C] text-sm font-semibold tracking-widest uppercase px-4 py-2 bg-[#FFE8F1] rounded-full mb-4">
            ✨ Choose Your Taste ✨
          </span>
          <h2 className="font-serif text-4xl md:text-5xl bg-gradient-to-r from-[#E91E8C] via-pink-600 to-rose-600 bg-clip-text text-transparent font-bold tracking-tight mt-4">
            Flavour Choices
          </h2>
          <p className="text-[#4A3540]/70 text-lg mt-6 max-w-2xl mx-auto">
            Explore our signature cake bases, each with delicious topping options to customize your perfect slice
          </p>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10"
        >
          {flavours.map((flavour, index) => (
            <motion.div
              key={flavour.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.12 }}
              whileHover={{ y: -8, transition: { duration: 0.3 } }}
              className="relative group h-full"
            >
              {/* Glow effect */}
              <div className={`absolute -inset-0.5 bg-gradient-to-r ${flavour.lightGradient.replace('from-', 'from-').replace('via-', 'via-').replace('to-', 'to-')} rounded-3xl opacity-0 group-hover:opacity-100 transition duration-300 blur-xl`}></div>
              
              {/* Card */}
              <div className={`relative flavour-card group bg-gradient-to-br ${flavour.lightGradient} rounded-3xl p-8 md:p-10 border-2 border-white/50 hover:border-white shadow-lg group-hover:shadow-2xl transition-all duration-300 h-full backdrop-blur-sm`}>
                
                {/* Sparkle effect corner */}
                <div className="absolute top-4 right-4 text-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300">✨</div>
                
                {/* Premium Badge */}
                {flavour.isPremium && (
                  <div className="absolute top-4 left-4 bg-gradient-to-r from-yellow-400 to-amber-400 text-amber-900 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest shadow-md">
                    ✨ Premium
                  </div>
                )}
                
                {/* Emoji container with glow */}
                <motion.div 
                  whileHover={{ scale: 1.2, rotate: 10 }}
                  transition={{ duration: 0.3 }}
                  className={`w-20 h-20 rounded-full bg-gradient-to-br ${flavour.gradient} flex items-center justify-center mb-6 shadow-lg group-hover:shadow-xl transition-all duration-300`}
                >
                  <span className="text-5xl drop-shadow-lg">{flavour.emoji}</span>
                </motion.div>

                {/* Content */}
                <h3 className={`font-serif text-2xl md:text-3xl ${flavour.color} font-bold mb-3 transition-colors duration-300`}>
                  {flavour.name}
                </h3>
                
                <p className={`${flavour.color}/70 text-base md:text-lg font-medium mb-6 transition-all duration-300`}>
                  {flavour.description}
                </p>

                {/* Bottom accent line */}
                <div className={`h-1.5 w-12 rounded-full bg-gradient-to-r ${flavour.gradient} mb-6`}></div>

                {/* Variations - Always Visible */}
                {flavour.variations.length > 0 && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-4">
                      <span className={`text-sm font-semibold uppercase tracking-wider ${flavour.color}`}>
                        ✨ Available Options
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {flavour.variations.map((variation, idx) => (
                        <motion.div
                          key={variation.name}
                          initial={{ opacity: 0, scale: 0.8, y: 10 }}
                          whileInView={{ opacity: 1, scale: 1, y: 0 }}
                          transition={{ duration: 0.4, delay: idx * 0.08 }}
                          whileHover={{ scale: 1.08, transition: { duration: 0.2 } }}
                          className={`relative group px-4 py-2 rounded-full text-xs md:text-sm font-bold transition-all duration-300 cursor-pointer
                            ${flavour.name === "Red Velvet Base" ? "bg-gradient-to-r from-red-300 to-pink-300 text-red-900 hover:shadow-lg hover:from-red-400 hover:to-pink-400" : ""}
                            ${flavour.name === "Vanilla Base" ? "bg-gradient-to-r from-yellow-300 to-amber-300 text-amber-900 hover:shadow-lg hover:from-yellow-400 hover:to-amber-400" : ""}
                            ${flavour.name === "Pistachio Base" ? "bg-gradient-to-r from-emerald-300 to-green-300 text-emerald-900 hover:shadow-lg hover:from-emerald-400 hover:to-green-400" : ""}
                            ${flavour.name === "Chocolate Sponge" ? "bg-gradient-to-r from-orange-300 to-amber-300 text-amber-900 hover:shadow-lg hover:from-orange-400 hover:to-amber-400" : ""}
                            border-2 border-white/60 backdrop-blur-sm`}
                        >
                          <span className="inline-block mr-1.5">{variation.icon}</span>
                          {variation.name}
                          <span className="absolute inset-0 rounded-full bg-white/0 group-hover:bg-white/10 transition-all duration-300"></span>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Premium badge - only for those without variations */}
                {flavour.variations.length === 0 && (
                  <div className="mt-6 pt-4 border-t border-white/30">
                    <p className={`text-xs uppercase font-semibold tracking-widest ${flavour.color}/60`}>
                      ⭐ Premium Quality
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Products/Menu Section
const MenuSection = ({ products }) => {
  const settings = useSettings();
  const [activeCategory, setActiveCategory] = useState("all");
  
  const categories = [
    { id: "all", name: "All", icon: Heart },
    { id: "cakes", name: "Cakes", icon: Cake },
    { id: "cupcakes", name: "Cupcakes", icon: CakeSlice },
    { id: "cookies", name: "Cookies", icon: Cookie },
  ];

  const filteredProducts = activeCategory === "all" 
    ? products 
    : products.filter(p => p.category === activeCategory);

  const getWhatsAppLink = (productName) => {
    const phone = settings ? settings.whatsapp.replace(/[^0-9]/g, '') : '31617354769';
    return `https://wa.me/${phone}?text=Hi! I'd like to order ${encodeURIComponent(productName)}`;
  };

  return (
    <section id="menu" className="py-24 md:py-32 bg-[#FFEEF5]" data-testid="menu-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <span className="text-[#E91E8C] text-sm font-semibold tracking-widest uppercase">
            Our Delights
          </span>
          <h2 className="font-serif text-4xl md:text-5xl text-[#4A3540] font-semibold tracking-tight mt-4">
            Fresh from the Oven
          </h2>
          <p className="text-[#4A3540]/70 text-lg mt-4 max-w-2xl mx-auto">
            Each creation is baked with love, using premium ingredients and time-honored recipes
          </p>
        </motion.div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((cat) => {
            const Icon = cat.icon;
            return (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`category-badge flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                  activeCategory === cat.id 
                    ? "bg-[#E91E8C] text-white" 
                    : "bg-[#FFD6E8] text-[#E91E8C] hover:bg-[#E91E8C] hover:text-white"
                }`}
                data-testid={`category-${cat.id}`}
              >
                <Icon size={16} />
                {cat.name}
              </button>
            );
          })}
        </div>

        {/* Products Grid */}
        <motion.div 
          layout
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          <AnimatePresence mode="popLayout">
            {filteredProducts.map((product, index) => (
              <motion.div
                key={product.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="product-card bg-white rounded-2xl overflow-hidden border border-[#FFBDD6] group"
                data-testid={`product-card-${product.id}`}
              >
                <div className="overflow-hidden bg-gradient-to-br from-[#FFE8F1] to-[#FFEEF5]">
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="product-image w-full h-auto object-contain"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-[#E91E8C] uppercase tracking-wider">
                      {product.category}
                    </span>
                    {product.is_featured && (
                      <span className="text-xs bg-[#FFD6E8] text-[#E91E8C] px-2 py-1 rounded-full">
                        Featured
                      </span>
                    )}
                  </div>
                  <h3 className="font-serif text-xl text-[#4A3540] font-semibold mb-2">
                    {product.name}
                  </h3>
                  <p className="text-[#4A3540]/60 text-sm mb-4 line-clamp-2">
                    {product.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="font-serif text-2xl text-[#E91E8C] font-semibold">
                      €{product.price.toFixed(2)}
                    </span>
                    <a
                      href={getWhatsAppLink(product.name)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-[#E91E8C] text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-[#D11A7D] transition-all duration-300 btn-scale"
                      data-testid={`order-${product.id}`}
                    >
                      Order
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="about" className="py-24 md:py-32 bg-[#FFD6E8]" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="aspect-[4/5] rounded-2xl overflow-hidden">
              <img
                src="https://staazease.blob.core.windows.net/open-resources/babucake.png"
                alt="Baker at work"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-[#E91E8C] text-white p-6 rounded-2xl shadow-xl">
              <p className="font-serif text-3xl font-semibold">5+</p>
              <p className="text-sm opacity-90">Years of Baking</p>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-[#E91E8C] text-sm font-semibold tracking-widest uppercase">
              Our Story
            </span>
            <h2 className="font-serif text-4xl md:text-5xl text-[#4A3540] font-semibold tracking-tight mt-4 mb-6">
              Baking Dreams into Reality
            </h2>
            <div className="space-y-4 text-[#4A3540]/70 text-lg leading-relaxed">
              <p>
                Hi, I’m Pragnya — some of you also know me as Poonam 🤍
I’m a proud mother of two and the heart behind Rcake Cottage. My love for baking started as a way to create wholesome treats for my family, but it quickly grew into a passion that I wanted to share with the world. I believe that food made with love can bring people together, and that’s exactly what I aim to do with every cake, cupcake, cookie, and pastry that comes out of my oven.                
              </p>
              <p>
                My baking journey began years ago, not as a business, but as a mother. Like many parents, I became more conscious about what was going into my children’s bodies. Seeing so many products filled with artificial and harmful ingredients made me pause and think — I wanted something better. Something real. Something made with care.
And that’s how Rcake Cottage was born.
Every cake, cupcake, and cookie is baked using quality, real ingredients — thoughtfully selected, carefully prepared, and made with love. My creations are never overly sweet and are always customized to suit each customer’s taste and celebration.
For me, baking is not just about desserts. It’s about being part of your special moments — birthdays, milestones, family gatherings — and making it more special, one celebration at a time.
              </p>
              <p>
                From birthdays to weddings, everyday treats to special celebrations, we're here 
                to make your moments sweeter. Let us be part of your story!
                Thank you for trusting me to be part of your cherished memories and for supporting this mom-owned dream 🤍
              </p>
            </div>
            <div className="flex items-center gap-4 mt-8">
              <div className="flex items-center gap-2 text-[#E91E8C]">
                <Heart className="fill-current" size={20} />
                <span className="font-medium">Made with Love</span>
              </div>
              <div className="w-px h-6 bg-[#4A3540]/20" />
              <div className="flex items-center gap-2 text-[#5BC0DE]">
                <Star className="fill-current" size={20} />
                <span className="font-medium">Premium Quality</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Why Choose Rcake Cottage Section
const WhyChooseSection = () => {
  const reasons = [
    {
      id: 1,
      icon: Leaf,
      title: "Made with Real, Quality Ingredients",
      description: "As a mother, I am deeply mindful of what goes into every bake. I choose quality ingredients and avoid unnecessary additives — because your family deserves cakes made with care and intention.",
      color: "from-[#10B981] to-[#059669]"
    },
    {
      id: 2,
      icon: Cake,
      title: "Perfectly Balanced Flavors",
      description: "Our cakes are soft, flavorful, and never overly sweet. Each recipe is thoughtfully crafted to create a balanced taste that both children and adults can enjoy.",
      color: "from-[#F59E0B] to-[#D97706]"
    },
    {
      id: 3,
      icon: CakeSlice,
      title: "Customized for Your Celebration",
      description: "Every celebration is unique, and your cake should be too. From flavors to designs and personalized messages, each order is made to reflect your special moment.",
      color: "from-[#3B82F6] to-[#1D4ED8]"
    },
    {
      id: 4,
      icon: Heart,
      title: "Handmade with Love & Attention to Detail",
      description: "Every cake, cupcake, and cookie is prepared in small batches with careful attention to detail — because I believe the little touches make the biggest difference.",
      color: "from-[#E91E8C] to-[#D11A7D]"
    },
    {
      id: 5,
      icon: Users,
      title: "A Mom-Owned Business You Can Trust",
      description: "When you order from Rcake Cottage, you're supporting a mother who truly understands how meaningful family celebrations are. I treat every order as if it were for my own home.",
      color: "from-[#8B5CF6] to-[#6D28D9]"
    }
  ];

  return (
    <section id="why-choose" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <span className="text-[#E91E8C] text-sm font-semibold tracking-widest uppercase">
            Why Choose Us
          </span>
          <h2 className="font-serif text-4xl md:text-5xl text-[#4A3540] font-semibold tracking-tight mt-4 mb-6">
            Why Choose Rcake Cottage?
          </h2>
          <p className="text-[#4A3540]/70 text-lg max-w-2xl mx-auto">
            Discover what makes our cakes special and why families trust us for their most cherished moments
          </p>
        </motion.div>

        {/* Reasons Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {reasons.map((reason, index) => {
            const IconComponent = reason.icon;
            return (
              <motion.div
                key={reason.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group h-full"
              >
                <div className="h-full bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 border border-gray-100 hover:border-[#E91E8C]/30 hover:shadow-xl transition-all duration-300">
                  {/* Icon Container */}
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-xl bg-gradient-to-br ${reason.color} mb-6 transform group-hover:scale-110 transition-transform duration-300`}>
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>

                  {/* Content */}
                  <h3 className="font-serif text-lg font-semibold text-[#4A3540] mb-4 leading-tight">
                    {reason.title}
                  </h3>
                  <p className="text-[#4A3540]/70 text-sm leading-relaxed">
                    {reason.description}
                  </p>

                  {/* Accent line */}
                  <div className={`h-1 w-12 bg-gradient-to-r ${reason.color} rounded-full mt-6 transform origin-left group-hover:w-full transition-all duration-300`}></div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Footer CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center mt-16"
        >
          <p className="text-[#4A3540]/70 text-lg mb-6 font-medium">
            Ready to experience the Rcake Cottage difference?
          </p>
          <a
            href="#contact"
            className="inline-flex items-center gap-2 bg-[#E91E8C] text-white px-8 py-4 rounded-full text-base font-medium hover:bg-[#D11A7D] transition-all duration-300 btn-scale shadow-lg shadow-[#E91E8C]/20"
          >
            Order Now
            <ChevronRight size={20} />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = ({ testimonials }) => {
  return (
    <section id="testimonials" className="py-24 md:py-32 bg-[#FFEEF5]" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <span className="text-[#E91E8C] text-sm font-semibold tracking-widest uppercase">
            Love Notes
          </span>
          <h2 className="font-serif text-4xl md:text-5xl text-[#4A3540] font-semibold tracking-tight mt-4">
            What Our Customers Say
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              className="testimonial-card bg-white p-8 rounded-2xl border border-[#FFBDD6]"
              data-testid={`testimonial-${testimonial.id}`}
            >
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} size={18} className="fill-[#E91E8C] text-[#E91E8C]" />
                ))}
              </div>
              <p className="text-[#4A3540]/70 text-base leading-relaxed mb-6 italic">
                "{testimonial.content}"
              </p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#FFD6E8] rounded-full flex items-center justify-center">
                  <span className="font-serif text-[#E91E8C] text-lg font-semibold">
                    {testimonial.customer_name.charAt(0)}
                  </span>
                </div>
                <span className="font-medium text-[#4A3540]">{testimonial.customer_name}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Instagram Section
const InstagramSection = () => {
  const settings = useSettings();
  const instagramUsername = settings?.instagram || 'rcake_cottage';
  const instagramLink = `https://instagram.com/${instagramUsername}`;

  const instagramImages = [
    "https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=400&q=80",
    "https://images.unsplash.com/photo-1614707267537-b85aaf00c4b7?w=400&q=80",
    "https://images.unsplash.com/photo-1499636136210-6f4ee915583e?w=400&q=80",
    "https://images.unsplash.com/photo-1555507036-ab1f4038808a?w=400&q=80",
    "https://images.unsplash.com/photo-1616541823729-00fe0aacd32c?w=400&q=80",
    "https://images.unsplash.com/photo-1587668178277-295251f900ce?w=400&q=80",
  ];

  return (
    <section className="py-24 md:py-32 bg-[#FFD6E8]" data-testid="instagram-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <span className="text-[#E91E8C] text-sm font-semibold tracking-widest uppercase">
            Follow Us
          </span>
          <h2 className="font-serif text-4xl md:text-5xl text-[#4A3540] font-semibold tracking-tight mt-4">
            @{instagramUsername}
          </h2>
          <p className="text-[#4A3540]/70 text-lg mt-4">
            Get inspired by our latest creations on Instagram
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {instagramImages.map((img, index) => (
            <motion.a
              key={index}
              href={instagramLink}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="insta-item aspect-square rounded-xl overflow-hidden group relative"
              data-testid={`instagram-${index}`}
            >
              <img
                src={img}
                alt={`Instagram post ${index + 1}`}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-[#E91E8C]/0 group-hover:bg-[#E91E8C]/30 transition-all duration-300 flex items-center justify-center">
                <Instagram className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" size={32} />
              </div>
            </motion.a>
          ))}
        </div>

        <div className="text-center mt-8">
          <a
            href={instagramLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white text-[#E91E8C] px-6 py-3 rounded-full font-medium hover:bg-[#E91E8C] hover:text-white transition-all duration-300 btn-scale"
            data-testid="follow-instagram-btn"
          >
            <Instagram size={20} />
            Follow on Instagram
          </a>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const settings = useSettings();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! We'll get back to you soon.");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-[#FFEEF5]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-[#E91E8C] text-sm font-semibold tracking-widest uppercase">
              Get in Touch
            </span>
            <h2 className="font-serif text-4xl md:text-5xl text-[#4A3540] font-semibold tracking-tight mt-4 mb-6">
              Let's Create Something Sweet Together
            </h2>
            <p className="text-[#4A3540]/70 text-lg leading-relaxed mb-8">
              Have a special occasion coming up? Want to place a custom order? 
              We'd love to hear from you! Fill out the form or reach us directly.
            </p>

            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#FFD6E8] rounded-full flex items-center justify-center">
                  <Phone size={20} className="text-[#E91E8C]" />
                </div>
                <div>
                  <p className="text-sm text-[#4A3540]/60">Call us</p>
                  <a href={`tel:${settings?.phone || '+31617354769'}`} className="text-[#4A3540] font-medium hover:text-[#E91E8C] transition-colors">
                    {settings?.phone || '+31617354769'}
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#FFD6E8] rounded-full flex items-center justify-center">
                  <Mail size={20} className="text-[#E91E8C]" />
                </div>
                <div>
                  <p className="text-sm text-[#4A3540]/60">Email us</p>
                  <a href={`mailto:${settings?.email || 'rcakecottage23@gmail.com'}`} className="text-[#4A3540] font-medium hover:text-[#E91E8C] transition-colors">
                    {settings?.email || 'rcakecottage23@gmail.com'}
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#FFD6E8] rounded-full flex items-center justify-center">
                  <MapPin size={20} className="text-[#E91E8C]" />
                </div>
                <div>
                  <p className="text-sm text-[#4A3540]/60">Location</p>
                  <p className="text-[#4A3540] font-medium">{settings?.address || 'Den Haag, Netherlands'}</p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <form onSubmit={handleSubmit} className="bg-white p-8 rounded-2xl border border-[#FFBDD6]" data-testid="contact-form">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-[#4A3540] mb-2">Name</label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your name"
                    required
                    className="form-input w-full border-[#FFBDD6] rounded-xl focus:border-[#E91E8C] focus:ring-[#E91E8C]/10"
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#4A3540] mb-2">Email</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your@email.com"
                    required
                    className="form-input w-full border-[#FFBDD6] rounded-xl focus:border-[#E91E8C] focus:ring-[#E91E8C]/10"
                    data-testid="contact-email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#4A3540] mb-2">Phone (optional)</label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+31 6 1234 5678"
                    className="form-input w-full border-[#FFBDD6] rounded-xl focus:border-[#E91E8C] focus:ring-[#E91E8C]/10"
                    data-testid="contact-phone"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#4A3540] mb-2">Message</label>
                  <Textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Tell us about your order or inquiry..."
                    required
                    rows={4}
                    className="form-input w-full border-[#FFBDD6] rounded-xl focus:border-[#E91E8C] focus:ring-[#E91E8C]/10 resize-none"
                    data-testid="contact-message"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#E91E8C] text-white py-6 rounded-full font-medium hover:bg-[#D11A7D] transition-all duration-300 btn-scale disabled:opacity-50"
                  data-testid="contact-submit"
                >
                  {isSubmitting ? (
                    "Sending..."
                  ) : (
                    <>
                      Send Message
                      <Send size={18} className="ml-2 inline" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const settings = useSettings();
  const whatsappLink = settings ? `https://wa.me/${settings.whatsapp.replace(/[^0-9]/g, '')}` : '#';
  const instagramLink = settings ? `https://instagram.com/${settings.instagram}` : '#';

  return (
    <footer className="bg-[#4A3540] text-white py-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
          <div>
             <p className="text-white/70 leading-relaxed">
              Handcrafted with love, served with a smile. Making your moments sweeter.
            </p>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Quick Links</h4>
            <div className="space-y-2">
              <a href="#home" className="block text-white/70 hover:text-[#E91E8C] transition-colors">Home</a>
              <a href="#menu" className="block text-white/70 hover:text-[#E91E8C] transition-colors">Menu</a>
              <a href="#about" className="block text-white/70 hover:text-[#E91E8C] transition-colors">About</a>
              <a href="#contact" className="block text-white/70 hover:text-[#E91E8C] transition-colors">Contact</a>
              <a href="/admin" className="block text-white/70 hover:text-[#E91E8C] transition-colors flex items-center gap-1">
                <Settings size={14} />
                Admin
              </a>
            </div>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Follow Us</h4>
            <div className="flex gap-4">
              <a
                href={instagramLink}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#E91E8C] transition-all duration-300"
                data-testid="footer-instagram"
              >
                <Instagram size={18} />
              </a>
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#E91E8C] transition-all duration-300"
                data-testid="footer-whatsapp"
              >
                <MessageCircle size={18} />
              </a>
            </div>
          </div>
        </div>
        
        <div className="border-t border-white/10 pt-8">
          <div className="text-center">
            <p className="font-serif text-3xl md:text-4xl text-[#E91E8C] font-semibold mb-2">
              Baked with 100% Love
            </p>
            <p className="text-white/50 text-sm">
              © {new Date().getFullYear()} RCake Cottage. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

// WhatsApp Floating Button
const WhatsAppButton = () => {
  const settings = useSettings();
  const whatsappLink = settings 
    ? `https://wa.me/${settings.whatsapp.replace(/[^0-9]/g, '')}?text=Hi! I'd like to place an order.`
    : '#';

  return (
    <motion.a
      href={whatsappLink}
      target="_blank"
      rel="noopener noreferrer"
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ delay: 1, type: "spring" }}
      className="whatsapp-btn fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg shadow-[#25D366]/30 hover:bg-[#20BA5A] transition-colors group"
      data-testid="whatsapp-btn"
    >
      <MessageCircle size={26} className="text-white" />
      <span className="absolute right-full mr-3 bg-white text-[#4A3540] px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
        Chat with us!
      </span>
    </motion.a>
  );
};

// Home Page Component
const HomePage = () => {
  const [products, setProducts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Seed data first
        await axios.post(`${API}/seed`);
        
        // Fetch products, testimonials, and settings
        const [productsRes, testimonialsRes, settingsRes] = await Promise.all([
          axios.get(`${API}/products`),
          axios.get(`${API}/testimonials`),
          axios.get(`${API}/settings`)
        ]);
        
        setProducts(productsRes.data);
        setTestimonials(testimonialsRes.data);
        setSettings(settingsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFEEF5] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#FFD6E8] border-t-[#E91E8C] rounded-full animate-spin mx-auto mb-4"></div>
          <p className="font-serif text-xl text-[#4A3540]">Loading sweet delights...</p>
        </div>
      </div>
    );
  }

  return (
    <SettingsContext.Provider value={settings}>
      <div className="bg-[#FFEEF5]">
        <div className="grain-overlay" />
        <Navbar />
        <HeroSection />
        <FlavourSection />
        <MenuSection products={products} />
        <AboutSection />
        <WhyChooseSection />
        <TestimonialsSection testimonials={testimonials} />
        <InstagramSection />
        <ContactSection />
        <Footer />
        <WhatsAppButton />
      </div>
    </SettingsContext.Provider>
  );
};

// Main App Component
function App() {
  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/admin" element={<AdminApp />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
